/* @jsx h */
import type { EpsonPrinterInfo } from "@app/vue/providers/bridge-rpc-types";
import { useLocalization } from "@lemmonhq/uikit";
import { defineComponent, h } from "vue";
import type { SetupError } from "./bluetoothPrinter";
import { PasswordField } from "./PasswordField";
import { RichText, StepHeader } from "./PrinterSetupLayout";
import { HeroIcon, SetupErrorMessage } from "./PrinterSetupSteps";
import { WifiNetworkPicker, type WifiScan } from "./PrinterWifiNetworkPicker";
import {
  IP_MODES,
  WIFI_SECURITY,
  type Ipv4Config,
  type PrinterWifiNetwork,
  type WifiCheck,
  type WifiConfig,
  type WifiFormError,
  type WifiSecurity,
} from "./printerWifi";

export type WifiPhase = "form" | "applying" | "verifying" | "applied";

const SECURITY_LABEL: Record<WifiSecurity, string> = {
  "WPA/WPA2-PSK": "WPA/WPA2",
  "WPA3-SAE(AES)": "WPA3",
};

const IP_FIELDS = [
  { key: "ip", label: "devices.bt_printer.ip_address_label" },
  { key: "subnetMask", label: "devices.bt_printer.ip_subnet_label" },
  { key: "gateway", label: "devices.bt_printer.ip_gateway_label" },
] as const;

const Ipv4Fields = defineComponent<{
  config: Ipv4Config;
  disabled: boolean;
  onChange: (config: Ipv4Config) => void;
}>({
  name: "PrinterIpv4Fields",
  props: ["config", "disabled", "onChange"],
  setup(props) {
    const { t } = useLocalization();
    const update = (patch: Partial<Ipv4Config>) =>
      props.onChange({ ...props.config, ...patch });

    return () => (
      <div class="space-y-2">
        <div class="form-control w-full">
          <span class="label-text mb-1">
            {t("devices.bt_printer.ip_mode_label")}
          </span>
          <div class="join w-full">
            {IP_MODES.map((mode) => (
              <button
                key={mode}
                type="button"
                disabled={props.disabled}
                class={[
                  "btn btn-sm join-item flex-1",
                  props.config.mode === mode ? "btn-primary" : "btn-outline",
                ]}
                onClick={() => update({ mode })}
              >
                {t(`devices.bt_printer.ip_mode_${mode.toLowerCase()}`)}
              </button>
            ))}
          </div>
        </div>
        {props.config.mode === "Manual" &&
          IP_FIELDS.map(({ key, label }) => (
            <label key={key} class="form-control w-full">
              <span class="label-text mb-1">{t(label)}</span>
              <input
                type="text"
                inputmode="decimal"
                autocomplete="off"
                class="input input-bordered h-10 min-h-10 w-full font-mono"
                placeholder={key === "subnetMask" ? "255.255.255.0" : ""}
                value={props.config[key]}
                disabled={props.disabled}
                onInput={(event: Event) =>
                  update({ [key]: (event.target as HTMLInputElement).value })
                }
              />
            </label>
          ))}
      </div>
    );
  },
});

// Typed network name, for app builds that can't list networks (iOS, older
// Android builds) or when the scan failed.
const SsidInput = defineComponent<{
  ssid: string;
  disabled: boolean;
  onInput: (ssid: string) => void;
}>({
  name: "PrinterWifiSsidInput",
  props: ["ssid", "disabled", "onInput"],
  setup(props) {
    const { t } = useLocalization();
    return () => (
      <label class="form-control w-full">
        <span class="label-text mb-1">
          {t("devices.bt_printer.wifi_ssid_label")}
        </span>
        <input
          type="text"
          autocomplete="off"
          autocapitalize="off"
          class="input input-bordered h-10 min-h-10 w-full"
          value={props.ssid}
          disabled={props.disabled}
          onInput={(event: Event) =>
            props.onInput((event.target as HTMLInputElement).value)
          }
        />
      </label>
    );
  },
});

const WifiForm = defineComponent<{
  config: WifiConfig;
  disabled: boolean;
  formError: WifiFormError | null;
  wifiScan: WifiScan | null;
  onChange: (config: WifiConfig) => void;
  onSubmit: () => void;
  onRescan: () => void;
}>({
  name: "PrinterWifiForm",
  props: [
    "config",
    "disabled",
    "formError",
    "wifiScan",
    "onChange",
    "onSubmit",
    "onRescan",
  ],
  setup(props) {
    const { t } = useLocalization();
    const update = (patch: Partial<WifiConfig>) =>
      props.onChange({ ...props.config, ...patch });
    // With only the picker on screen there is no name to type.
    const formErrorKey = () =>
      props.formError === "ssid_required" &&
      props.wifiScan &&
      props.wifiScan.status !== "failed"
        ? "network_required"
        : props.formError;

    return () => (
      <form
        class="w-full max-w-sm space-y-2.5 text-left"
        onSubmit={(event: Event) => {
          event.preventDefault();
          props.onSubmit();
        }}
      >
        {props.wifiScan && (
          <WifiNetworkPicker
            scan={props.wifiScan}
            selectedSsid={props.config.ssid}
            disabled={props.disabled}
            onSelect={({ ssid, security }: PrinterWifiNetwork) =>
              update({ ssid, security })
            }
            onRescan={props.onRescan}
          />
        )}
        {(!props.wifiScan || props.wifiScan.status === "failed") && (
          <SsidInput
            ssid={props.config.ssid}
            disabled={props.disabled}
            onInput={(ssid: string) => update({ ssid })}
          />
        )}
        <label class="form-control w-full">
          <span class="label-text mb-1">
            {t("devices.bt_printer.wifi_security_label")}
          </span>
          <select
            class="select select-bordered select-sm h-10 min-h-10 w-full text-sm"
            value={props.config.security}
            disabled={props.disabled}
            onChange={(event: Event) =>
              update({
                security: (event.target as HTMLSelectElement)
                  .value as WifiSecurity,
              })
            }
          >
            {WIFI_SECURITY.map((security) => (
              <option key={security} value={security}>
                {SECURITY_LABEL[security]}
              </option>
            ))}
          </select>
        </label>
        <label class="form-control w-full">
          <span class="label-text mb-1">
            {t("devices.bt_printer.wifi_passphrase_label")}
          </span>
          <PasswordField
            value={props.config.passphrase}
            disabled={props.disabled}
            onInput={(passphrase: string) => update({ passphrase })}
          />
        </label>
        <Ipv4Fields
          config={props.config.ipv4}
          disabled={props.disabled}
          onChange={(ipv4: Ipv4Config) => update({ ipv4 })}
        />
        {props.formError && (
          <p class="text-sm text-error">
            {t(`devices.bt_printer.wifi_${formErrorKey()}`)}
          </p>
        )}
      </form>
    );
  },
});

const NetworkPrinters = defineComponent<{
  isSearching: boolean;
  searchAttempted: boolean;
  printers: EpsonPrinterInfo[];
}>({
  name: "PrinterWifiNetworkPrinters",
  props: ["isSearching", "searchAttempted", "printers"],
  setup(props) {
    const { t } = useLocalization();
    return () => {
      if (props.isSearching || !props.searchAttempted) return null;
      if (props.printers.length === 0) {
        return (
          <p class="mt-4 max-w-xs text-xs text-base-content/50">
            {t("devices.bt_printer.wifi_not_found")}
          </p>
        );
      }
      return (
        <div class="mt-4 w-full max-w-sm space-y-2">
          {props.printers.map((printer) => (
            <div
              key={printer.target}
              class="flex items-center gap-3 rounded-xl bg-base-200 px-3 py-2 text-left ring-1 ring-base-content/5"
            >
              <span class="material-symbols-outlined text-2xl text-primary">
                wifi
              </span>
              <div class="min-w-0 flex-1">
                <div class="truncate text-sm font-semibold text-accent">
                  {printer.deviceName}
                </div>
                <div class="truncate font-mono text-xs text-base-content/50">
                  {printer.ipAddress || printer.target}
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    };
  },
});

const securityLabel = (security: string) =>
  SECURITY_LABEL[security as WifiSecurity] ?? security;

const WifiCheckResult = defineComponent<{ check: WifiCheck }>({
  name: "PrinterWifiCheckResult",
  props: ["check"],
  setup(props) {
    const { t } = useLocalization();
    return () => {
      const { check } = props;
      if (check.status === "unreachable") {
        return (
          <div class="mt-4 w-full max-w-sm rounded-2xl border border-warning/30 bg-warning/10 p-4 text-left text-sm text-warning">
            {t("devices.bt_printer.wifi_check_unreachable")}
            {check.detail && (
              <div class="mt-2 break-words font-mono text-xs text-warning/70">
                {check.detail}
              </div>
            )}
          </div>
        );
      }
      const { saved } = check;
      const network = (
        <RichText
          text={t(
            saved.ssid
              ? `devices.bt_printer.wifi_check_${check.status}`
              : "devices.bt_printer.wifi_check_no_network",
          )}
          values={{
            ssid: saved.ssid,
            security: securityLabel(saved.security),
          }}
        />
      );
      if (check.status === "mismatch") {
        return (
          <div class="mt-4 w-full max-w-sm rounded-2xl border border-error/30 bg-error/10 p-4 text-left text-sm text-error">
            {network}
          </div>
        );
      }
      return (
        <div class="mt-4 w-full max-w-sm rounded-2xl border border-success/30 bg-success/10 p-4 text-left text-sm">
          <div class="flex gap-2 text-success">
            <span class="material-symbols-outlined">check_circle</span>
            <span class="text-base-content/80">{network}</span>
          </div>
          <p class="mt-2 text-xs text-base-content/60">
            {t("devices.bt_printer.wifi_check_match_hint")}
          </p>
        </div>
      );
    };
  },
});

const CHECK_TITLE: Record<WifiCheck["status"], string> = {
  match: "devices.bt_printer.wifi_applied_title",
  mismatch: "devices.bt_printer.wifi_check_mismatch_title",
  unreachable: "devices.bt_printer.wifi_check_unreachable_title",
};

export const WifiStep = defineComponent<{
  printerName: string;
  phase: WifiPhase;
  check: WifiCheck | null;
  config: WifiConfig;
  formError: WifiFormError | null;
  wifiScan: WifiScan | null;
  error: SetupError | null;
  isSearchingNetwork: boolean;
  networkSearchAttempted: boolean;
  networkPrinters: EpsonPrinterInfo[];
  onChange: (config: WifiConfig) => void;
  onSubmit: () => void;
  onSearchNetwork: () => void;
  onRescanWifi: () => void;
}>({
  name: "PrinterSetupWifiStep",
  props: [
    "printerName",
    "phase",
    "check",
    "config",
    "formError",
    "wifiScan",
    "error",
    "isSearchingNetwork",
    "networkSearchAttempted",
    "networkPrinters",
    "onChange",
    "onSubmit",
    "onSearchNetwork",
    "onRescanWifi",
  ],
  setup(props) {
    const { t } = useLocalization();
    return () => {
      if (props.phase === "verifying") {
        return (
          <div class="flex flex-col items-center justify-center px-4 pb-4 pt-2 text-center">
            <HeroIcon icon="restart_alt" isAnimating />
            <StepHeader
              title={t("devices.bt_printer.wifi_verifying_title")}
              text={t("devices.bt_printer.wifi_applied_desc")}
              values={{ name: props.printerName, ssid: props.config.ssid }}
            />
            <p class="max-w-xs text-sm text-base-content/50">
              {t("devices.bt_printer.wifi_verifying_hint")}
            </p>
          </div>
        );
      }

      if (props.phase === "applied" && props.check) {
        const { check } = props;
        return (
          <div class="flex flex-col items-center justify-center px-4 pb-4 pt-2 text-center">
            <HeroIcon
              icon={check.status === "mismatch" ? "wifi_off" : "wifi"}
              isAnimating={props.isSearchingNetwork}
              isError={check.status === "mismatch"}
            />
            <StepHeader
              title={t(CHECK_TITLE[check.status])}
              text="{name}"
              values={{ name: props.printerName }}
            />
            <WifiCheckResult check={check} />
            {check.status !== "mismatch" && (
              <button
                onClick={props.onSearchNetwork}
                disabled={props.isSearchingNetwork}
                class="btn btn-outline btn-sm mt-4"
              >
                {props.isSearchingNetwork ? (
                  <span class="loading loading-spinner loading-sm" />
                ) : (
                  <span class="material-symbols-outlined">search</span>
                )}
                {t("devices.bt_printer.wifi_find_on_network")}
              </button>
            )}
            <NetworkPrinters
              isSearching={props.isSearchingNetwork}
              searchAttempted={props.networkSearchAttempted}
              printers={props.networkPrinters}
            />
            {props.error && <SetupErrorMessage error={props.error} />}
          </div>
        );
      }

      return (
        <div class="flex flex-col items-center justify-center px-4 pb-4 pt-2 text-center">
          <HeroIcon
            icon="wifi"
            isAnimating={props.phase === "applying"}
            isError={!!props.error}
          />
          <StepHeader
            title={t("devices.bt_printer.wifi_title")}
            text={
              props.printerName
                ? t("devices.bt_printer.wifi_desc")
                : t("devices.bt_printer.wifi_choose_desc")
            }
            values={{ name: props.printerName }}
          />
          <WifiForm
            config={props.config}
            disabled={props.phase === "applying"}
            formError={props.formError}
            wifiScan={props.wifiScan}
            onChange={props.onChange}
            onSubmit={props.onSubmit}
            onRescan={props.onRescanWifi}
          />
          {props.error && <SetupErrorMessage error={props.error} />}
        </div>
      );
    };
  },
});
